import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'

const GUEST_USER_UPDATE_MUTATION = gql`
  mutation ($input: UpdateGuestUserInput!) {
    updateGuestUser(input: $input) {
      user {
        id
        name
        email
        phoneNumber
        countryCode
        guestUserType
        mobileVerified
      }
      authToken
      success
      message
      errors {
        field
        message
      }
    }
  }
`

export interface GuestUserUpdateInput {
  claimUser?: boolean
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  countryCode?: string
  hearAboutUs?: string
  otherHearAboutUs?: string
  password?: string
  terms?: boolean
  guestUserId?: string
  mobileVerified?: boolean
  cartToken?: string
}

interface UpdateGuestUserMutation {
  user: User | null
  authToken: string | null
  success: boolean
  message: string
  errors: MutationError[]
}

interface UpdateGuestUserMutationResponse {
  updateGuestUser: UpdateGuestUserMutation
}

export const useGuestUserUpdateApi = (): UseMutationResult<
  UpdateGuestUserMutation,
  Error,
  GuestUserUpdateInput,
  unknown
> => {
  const client = useGraphQLClient()
  const queryClient = useQueryClient()

  const guestUserUpdateApi = async (
    input: GuestUserUpdateInput,
  ): Promise<UpdateGuestUserMutation> => {
    const result = await client.request<UpdateGuestUserMutationResponse>(
      GUEST_USER_UPDATE_MUTATION,
      {
        input,
      },
    )

    return result.updateGuestUser
  }

  return useMutation<UpdateGuestUserMutation, Error, GuestUserUpdateInput>({
    mutationFn: guestUserUpdateApi,
    // Claiming a guest turns the same user id (and token) into a full account
    // with no reload, so the `user { smsSubscription }` read never re-runs on
    // its own. Refetch it: that read is where the backend ties an SMS opt-in
    // made while logged out to the account by its phone number (GSE-12330),
    // and the order-confirmation card keyed on it should drop out on the page
    // where the account was just made.
    onSuccess: (data, variables) => {
      if (variables.claimUser && data.success) {
        void queryClient.invalidateQueries({ queryKey: ['smsSubscription'] })
      }
    },
  })
}
